import {
  Empty_NoSqlDoc,
  FBDTKeygenGeneric,
  MakeFBDocType,
  NoSqlDoc,
  Z_NoSqlDoc,
} from '@rabbit/firebase/doctype';
import { z, schema } from '@rabbit/utils/ts';

/* -------------------------------------------------------------------------- */
/*                          Algolia Configuration Types                       */
/* -------------------------------------------------------------------------- */

/**
 * Algolia search configuration for tenant-specific optimizations.
 * This allows dynamic configuration of search and retrieval attributes
 * based on what each tenant displays in their registration table.
 */

export interface AlgoliaSearchSettings {
  /** Maximum number of hits per page for searches */
  hitsPerPage?: number;
  
  /** Enable/disable typo tolerance */
  typoTolerance?: boolean;
  
  /** Minimum word size for 1 typo */
  minWordSizefor1Typo?: number;
  
  /** Minimum word size for 2 typos */
  minWordSizefor2Typos?: number;
  
  /** Maximum values per facet */
  maxValuesPerFacet?: number;
  
  /** Pagination limit */
  paginationLimitedTo?: number;
}

export const Z_AlgoliaSearchSettings = z.object({
  hitsPerPage: z.number().optional(),
  typoTolerance: z.boolean().optional(),
  minWordSizefor1Typo: z.number().optional(),
  minWordSizefor2Typos: z.number().optional(),
  maxValuesPerFacet: z.number().optional(),
  paginationLimitedTo: z.number().optional(),
});

export interface DTAlgoliaConfig extends NoSqlDoc {
  /** Tenant identifier (e.g., 'SUPAGARD', 'WARRANTYIRELAND') */
  tenantLink: string;
  
  /** Human-readable description of this configuration */
  description: string;
  
  /** Whether this configuration is currently active */
  isActive: boolean;

  /** Type of business entity this configuration applies to */
  entityType: 'holding_proxy' | 'consumer' | 'claims' | 'products' | 'retailer' | string;
  
  /** Algolia search settings */
  searchSettings?: AlgoliaSearchSettings;
  
  /** Searchable attributes for Algolia (ready to use format) */
  searchableAttributes: string[];
  
  /** Attributes to retrieve from Algolia (ready to use format) */
  attributesToRetrieve: string[];
  
  /** Column ID to Algolia field name mappings for sorting */
  sorting?: Record<string, string>;

  /** Filters for Algolia */
  filters?: Record<string, string>;
  
  /** Configuration version for tracking changes */
  version: string;
  
  /** Created by user ID */
  createdBy?: string;
  
  /** Last modified by user ID */
  lastModifiedBy?: string;
  
  /** Last modified timestamp */
  lastModified?: number;
  
  /** Configuration notes for documentation */
  notes?: string;
}

export const Z_DTAlgoliaConfig = schema<DTAlgoliaConfig>()(
  z.object({
    tenantLink: z.string(),
    description: z.string(),
    isActive: z.boolean(),
    entityType: z.union([
      z.enum(['holding_proxy', 'consumer', 'claims', 'products', 'retailer']),
      z.string()
    ]),
    searchSettings: Z_AlgoliaSearchSettings.optional(),
    searchableAttributes: z.array(z.string()),
    attributesToRetrieve: z.array(z.string()),
    version: z.string(),
    createdBy: z.string().optional(),
    lastModifiedBy: z.string().optional(),
    lastModified: z.number().optional(),
    notes: z.string().optional(),
  }).merge(Z_NoSqlDoc)
);

export const FBD_AlgoliaConfig = MakeFBDocType<DTAlgoliaConfig>({
  name: 'Algolia_Config',
  collection: 'algolia_config',
  empty: () => {
    const result: DTAlgoliaConfig = {
      ...Empty_NoSqlDoc(),
      tenantLink: '',
      description: '',
      isActive: true,
      entityType: 'holding_proxy',
      searchableAttributes: [],
      attributesToRetrieve: [],
      sorting: {},
      filters: {},
      version: '1.0.0',
    };
    return result;
  },
  keygen: FBDTKeygenGeneric,
});

/* -------------------------------------------------------------------------- */
/*                              Helper Types                                 */
/* -------------------------------------------------------------------------- */

/**
 * Processed configuration ready for use with Algolia SearchOptions
 */
export interface ProcessedAlgoliaConfig {
  /** Tenant identifier */
  tenantLink: string;
  
  /** Configuration description */
  description: string;
  
  /** Searchable attributes for Algolia */
  searchableAttributes: string[];
  
  /** Attributes to retrieve from Algolia */
  attributesToRetrieve: string[];
  
  /** Search settings */
  searchSettings: AlgoliaSearchSettings;
  
  /** Column ID to Algolia field name mappings for sorting */
  sorting?: Record<string, string>;

  /** Filters for Algolia */
  filters?: Record<string, string>;
  
  /** Configuration version */
  version: string;
}

/**
 * Cache metadata for storing configurations in browser storage
 */
export interface AlgoliaConfigCache {
  /** Cached configurations by tenant link */
  configs: Record<string, ProcessedAlgoliaConfig>;
  
  /** Cache timestamp */
  timestamp: number;
  
  /** Cache expiration time in milliseconds */
  expiration: number;
  
  /** Cache version for invalidation */
  version: string;
}
